import { Children, isValidElement, type ReactNode } from "react";
import { parser, RuleType } from "markdown-to-jsx";
import { ResearchCitation } from "./ResearchCitations";

function citationId(child: ReactNode): string | undefined {
  if (!isValidElement<{ href?: string }>(child)) return;
  const href = child.props.href;
  return href?.match(/^#source-([a-zA-Z0-9_-]+)$/)?.[1];
}

export function groupResearchCitations(
  children: ReactNode[],
  messageId: string
) {
  const nodes = Children.toArray(children);
  const grouped: ReactNode[] = [];
  for (let i = 0; i < nodes.length; i++) {
    const id = citationId(nodes[i]);
    if (!id) {
      grouped.push(nodes[i]);
      continue;
    }
    const ids = [id];
    while (i + 1 < nodes.length) {
      let next = i + 1;
      if (
        typeof nodes[next] === "string" &&
        /^\s*$/.test(nodes[next] as string)
      )
        next++;
      const nextId = citationId(nodes[next]);
      if (!nextId) break;
      ids.push(nextId);
      i = next;
    }
    grouped.push(
      <ResearchCitation
        key={`citation-${i}`}
        sourceIds={[...new Set(ids)]}
        messageId={messageId}
      />
    );
  }
  return grouped;
}

export function researchCitationIds(markdown: string): string[] {
  if (!markdown.includes("#source-")) return [];
  const ids = new Set<string>();
  const visit = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(visit);
    } else if (value && typeof value === "object") {
      const node = value as Record<string, unknown>;
      if (node.type === RuleType.link && typeof node.target === "string") {
        const id = node.target.match(/^#source-([a-zA-Z0-9_-]+)$/)?.[1];
        if (id) ids.add(id);
      }
      Object.values(node).forEach(visit);
    }
  };
  visit(parser(markdown, { disableParsingRawHTML: true }));
  return [...ids];
}
